import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { GET_USER_RESET, UPDATE_USER_RESET } from "../Constants/Constants";
import {
  createUser,
  getAllCountry,
  getUserByID,
  getUserPermission,
  updateUser,
} from "../redux/actions/AdminAction";
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

export default function User() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const allProductIDs = [101, 102, 103, 104, 105, 106, 107]; // Add all product IDs here
  const [snackOpen, setSnackOpen] = useState(false);
  const [first_name, setFirst_name] = useState("");
  const [last_name, setLast_name] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [role, setRole] = useState("");
  const [countryScope, setCountryScope] = useState("all");
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [contact_number, setContact_number] = useState("");
  const [contact_number_error, setContact_number_error] = useState("");
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorAlertTitle, setErrorAlertTitle] = useState("");
  const [errorAlertMessage, setErrorAlertMessage] = useState("");
  const [selectedProducts, setSelectedProducts] = useState({});

  const CreateUser = useSelector((state) => state.CreateUser);
  const { error, userInfo } = CreateUser;

  const GetUserByID = useSelector((state) => state.GetUserByID);
  const { userById } = GetUserByID;

  const UpdateUser = useSelector((state) => state.UpdateUser);
  const { success, message, updateUserError } = UpdateUser;

  const GetUserPermissionData = useSelector(
    (state) => state.GetUserPermissionData
  );
  const { userPermissionInfo } = GetUserPermissionData;

  const GetAllCountry = useSelector((state) => state.GetAllCountry);
  const { countryInfo } = GetAllCountry;

  useEffect(() => {
    dispatch({ type: GET_USER_RESET });
    dispatch({ type: UPDATE_USER_RESET });
  }, []);

  useEffect(() => {
    if (id === "new") {
      setFirst_name("");
      setLast_name("");
      setEmail("");
      setRole("");
      setContact_number("");
      dispatch({ type: GET_USER_RESET });
      dispatch({ type: UPDATE_USER_RESET });
    } else {
      dispatch({ type: UPDATE_USER_RESET });
      dispatch(getUserByID(id));
      dispatch(getUserPermission(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (userById) {
      setFirst_name(userById?.first_name?.trim() || "");
      setLast_name(userById?.last_name?.trim() || "");
      setEmail(userById?.email?.trim() || "");
      setRole(userById?.role?.trim() || "");
      setContact_number(userById?.contact_number?.trim() || "");
      setCountryScope(
        userById?.is_all_countries === null ||
          userById?.is_all_countries === undefined
          ? "all"
          : userById?.is_all_countries
          ? "all"
          : "multiple"
      );
      setSelectedCountries(userById?.selected_countries || []);
    }
  }, [userById]);

  useEffect(() => {
    if (userPermissionInfo) {
      const transformedProducts = userPermissionInfo.reduce((acc, item) => {
        const allPermissionsFalse =
          !item.can_add && !item.can_edit && !item.can_delete && !item.can_view;

        if (!allPermissionsFalse) {
          acc[item.product_id] = {
            can_add: item.can_add,
            can_edit: item.can_edit,
            can_delete: item.can_delete,
            can_view: item.can_view,
          };
        }
        return acc;
      }, {});
      setSelectedProducts(transformedProducts);
    }
  }, [userPermissionInfo]);

  useEffect(() => {
    if (countryScope === "multiple") {
      dispatch(getAllCountry());
    }
  }, [countryScope]);

  const validateEmail = (email) => {
    // Basic email validation regex
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmail = (event) => {
    const enteredValues = event.target.value
      .split("\n")
      .map((value) => value?.trim());

    const invalidEmails = enteredValues.filter(
      (email) => email && !validateEmail(email)
    );

    if (invalidEmails.length > 0) {
      setEmailError(`Invalid email addresses: ${invalidEmails.join(", ")}`);
    } else {
      setEmailError("");
    }
    const enteredEmail = enteredValues.join(" ");

    setEmail(enteredEmail);
  };

  const validateContactNumber = (number) => {
    // Validation: Allow numbers with or without + and country code
    // Option 1: + followed by 10-15 digits
    // Option 2: 10-15 digits
    const regex = /^(?:\+\d{1,3})?\d{10,15}$/;
    return regex.test(number);
  };

  const handleContactNumber = (e) => {
    const number = e.target.value;

    // Allow only valid characters: digits and +
    if (/^[+\d]*$/.test(number)) {
      setContact_number(number);

      if (number && !validateContactNumber(number)) {
        setContact_number_error(
          "Telephone number should be 10-15 digits, with or without a leading + and country code."
        );
      } else {
        setContact_number_error("");
      }
    }
  };

  const handleSnackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackOpen(false);
    setErrorAlert(false);
  };

  // Handle product selection
  const handleProductChange = (event, productId) => {
    const isChecked = event.target.checked;

    setSelectedProducts((prevSelected) => {
      const updatedProducts = { ...prevSelected };

      if (!isChecked) {
        // Remove or reset product based on type
        if (productId === 105) {
          // Keep product 105 but reset all permissions
          updatedProducts[productId] = {
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_view: false,
          };
        } else {
          // Remove other products when unchecked
          delete updatedProducts[productId];
        }
      } else {
        // Add product with appropriate default permissions
        const hasDefaultView = [102, 103, 105].includes(productId);
        updatedProducts[productId] = {
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_view: hasDefaultView,
        };
      }

      return updatedProducts;
    });
  };

  // Handle Add/Edit/Delete permission change
  const handlePermissionChange = (event, productId, permissionType) => {
    // If user is admin, only allow permission changes for product 105
    if (role === "admin" && productId !== 105) {
      return; // Don't allow permission changes for other products for admin
    }

    setSelectedProducts((prevSelected) => {
      const updatedProducts = { ...prevSelected };

      // Ensure the product exists in the state
      if (!updatedProducts[productId]) {
        updatedProducts[productId] = {
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_view: false,
        };
      }

      // Update the specific permission
      updatedProducts[productId] = {
        ...updatedProducts[productId],
        [permissionType]: event.target.checked,
      };

      // For product 105 (Manage Customer Logins), auto-check view when add or edit is checked
      if (productId === 105) {
        if (
          (permissionType === "can_add" || permissionType === "can_edit") &&
          event.target.checked
        ) {
          updatedProducts[productId].can_view = true; // Auto-check view
        }

        // If view is unchecked, uncheck add and edit as well (they depend on view)
        if (permissionType === "can_view" && !event.target.checked) {
          updatedProducts[productId].can_add = false;
          updatedProducts[productId].can_edit = false;
        }
      }

      // For other products, apply similar logic if needed
      if (productId !== 105) {
        if (
          (permissionType === "can_add" ||
            permissionType === "can_edit" ||
            permissionType === "can_delete") &&
          event.target.checked
        ) {
          updatedProducts[productId].can_view = true; // Auto-check view for any action
        }

        // If view is unchecked, uncheck all other permissions
        if (permissionType === "can_view" && !event.target.checked) {
          updatedProducts[productId].can_add = false;
          updatedProducts[productId].can_edit = false;
          updatedProducts[productId].can_delete = false;
        }
      }

      return updatedProducts;
    });
  };

  const handleSetSelectedCountries = (event, newValue) => {
    const selectedCodes = newValue.map((country) =>
      country.country_code.trim()
    );
    setSelectedCountries(selectedCodes);
  };

  const handleSubmit = () => {
    if (emailError || contact_number_error) {
      setErrorAlertTitle("Invalid values in the field.");
      setErrorAlertMessage("Please enter the fields properly.");
      setErrorAlert(true);
      setSnackOpen(true);
      return;
    }
    if (first_name && email && role) {
      let user_data;

      if (role === "user") {
        if (Object.keys(selectedProducts).length === 0) {
          setErrorAlertTitle("Required field error");
          setErrorAlertMessage("Please enter the required fields.");
          setSnackOpen(true);
          setErrorAlert(true);
          return;
        }

        const selectedProductsWithDefaults = {
          ...selectedProducts,
          102: {
            can_add: false,
            can_edit: false,
            can_delete: false,
            can_view: true, // Always true
          },
          104: {
            can_add: selectedProducts[104]?.can_add || false,
            can_edit: selectedProducts[104]?.can_edit || false,
            can_delete: selectedProducts[104]?.can_delete || false,
            can_view: true, // Always true for Manage Customers
          },
        };

        // Remove product 105 from user permissions
        if (selectedProductsWithDefaults[105]) {
          delete selectedProductsWithDefaults[105];
        }
        // Filter allProductIDs to exclude 105 for users
        const userProductIDs = allProductIDs.filter((id) => id !== 105);

        const allProductsWithPermissions = userProductIDs.reduce(
          (acc, productId) => {
            acc[productId] = selectedProductsWithDefaults[productId] || {
              can_add: false,
              can_edit: false,
              can_delete: false,
              can_view: false,
            };
            return acc;
          },
          {}
        );

        const hasValidPermissions = Object.values(
          allProductsWithPermissions
        ).some(
          (product) =>
            product.can_add ||
            product.can_edit ||
            product.can_delete ||
            product.can_view
        );

        if (!hasValidPermissions) {
          setErrorAlertTitle("No Permissions Selected");
          setErrorAlertMessage("Please select at least one permission.");
          setErrorAlert(true);
          setSnackOpen(true);
          return;
        }

        user_data = {
          first_name: first_name,
          last_name: last_name,
          email: email,
          password: email,
          role: role,
          contact_number: contact_number,
          is_all_countries: countryScope === "all" ? true : false,
          selected_countries: countryScope === "all" ? [] : selectedCountries,
          selectedProducts: allProductsWithPermissions,
        };
      } else if (role === "admin") {
        // For admin, only include product 105 permissions
        const adminProducts = {};
        if (selectedProducts[105]) {
          adminProducts[105] = selectedProducts[105];
        }

        user_data = {
          first_name: first_name,
          last_name: last_name,
          email: email,
          password: email,
          role: role,
          contact_number: contact_number,
          is_all_countries: countryScope === "all" ? true : false,
          selected_countries: countryScope === "all" ? [] : selectedCountries,
          selectedProducts: adminProducts, // Only product 105 permissions
        };
      } else {
        // For superuser
        user_data = {
          first_name: first_name,
          last_name: last_name,
          email: email,
          password: email,
          role: role,
          contact_number: contact_number,
          is_all_countries: countryScope === "all" ? true : false,
          selected_countries: countryScope === "all" ? [] : selectedCountries,
          selectedProducts: {},
        };
      }

      if (id === "new") {
        dispatch(createUser(user_data));
        setSnackOpen(true);

        setTimeout(() => {
          setFirst_name("");
          setLast_name("");
          setEmail("");
          setRole("");
          setContact_number("");
          setSelectedProducts({});
          navigate("/manageusers");
        }, 3000);
      } else {
        const updatedUser_data = {
          first_name: first_name,
          last_name: last_name,
          email: email,
          role: role,
          contact_number: contact_number,
          is_all_countries: countryScope === "all" ? true : false,
          selected_countries: countryScope === "all" ? [] : selectedCountries,
          selectedProducts:
            role === "user"
              ? user_data.selectedProducts
              : role === "admin"
              ? user_data.selectedProducts
              : {},
        };
        dispatch(updateUser(id, updatedUser_data));
        setSnackOpen(true);
        setTimeout(() => {
          navigate("/manageusers");
        }, 3000);
      }
    } else {
      setErrorAlertTitle("Required field error");
      setErrorAlertMessage("Please enter the required fields.");
      setSnackOpen(true);
      setErrorAlert(true);
    }
  };

  return (
    <Container sx={{ marginBottom: "5%" }}>
      <Box>
        <Box sx={{ margin: 3, fontSize: 25 }}>
          Manage Users
          <Divider sx={{ marginTop: 2 }}></Divider>
        </Box>
        <Box sx={{ fontSize: 25, margin: 3 }}>
          {id === "new" ? "Add User" : "Edit User"}
        </Box>
        <Box sx={{ margin: 3 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Grid item xs={6}>
                First Name
              </Grid>
              <Grid item xs={6} mt={1}>
                <TextField
                  fullWidth
                  required
                  value={first_name}
                  onChange={(e) => setFirst_name(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={6} mt={1}>
                Last Name
              </Grid>
              <Grid item xs={6} mt={1}>
                <TextField
                  fullWidth
                  required
                  value={last_name}
                  onChange={(e) => setLast_name(e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid item xs={6} mt={1}>
                Email
              </Grid>
              <Grid item xs={6} mt={1}>
                <TextField
                  fullWidth
                  required
                  value={email}
                  error={!!emailError}
                  helperText={emailError}
                  onChange={handleEmail}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt={1}>
              <FormControl>
                <FormLabel sx={{ color: "black !important" }}>Role</FormLabel>
                <RadioGroup
                  row
                  defaultValue={role}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <FormControlLabel
                    value="user"
                    control={<Radio />}
                    label="User"
                  />
                  <FormControlLabel
                    value="admin"
                    control={<Radio />}
                    label="Admin"
                  />
                  <FormControlLabel
                    value="superuser"
                    control={<Radio />}
                    label="Superuser"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {role == "user" ? (
              <Grid item xs={12} mt={1}>
                <FormControl>
                  <FormLabel sx={{ color: "black !important" }}>
                    Product Permission
                  </FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={101}
                          checked={!!selectedProducts[101]}
                          onChange={(event) => handleProductChange(event, 101)}
                        />
                      }
                      label="HX Killer"
                    />
                    <Grid container direction="row" spacing={2} mt={1}>
                      {selectedProducts[101] && (
                        <Grid container direction="row" ml={3} spacing={1}>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[101]?.can_add}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      101,
                                      "can_add"
                                    )
                                  }
                                />
                              }
                              label="Add"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[101]?.can_edit}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      101,
                                      "can_edit"
                                    )
                                  }
                                />
                              }
                              label="Edit"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[101]?.can_delete}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      101,
                                      "can_delete"
                                    )
                                  }
                                />
                              }
                              label="Delete"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[101]?.can_view}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      101,
                                      "can_view"
                                    )
                                  }
                                />
                              }
                              label="View"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <FormControlLabel
                      sx={{ marginTop: 1 }}
                      control={
                        <Checkbox
                          value={106}
                          checked={!!selectedProducts[106]}
                          onChange={(event) => handleProductChange(event, 106)}
                        />
                      }
                      label="Agency Booking Report"
                    />
                    <Grid container direction="row" spacing={2} mt={1}>
                      {selectedProducts[106] && (
                        <Grid container direction="row" ml={3} spacing={1}>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[106]?.can_add}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      106,
                                      "can_add"
                                    )
                                  }
                                />
                              }
                              label="Add"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[106]?.can_edit}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      106,
                                      "can_edit"
                                    )
                                  }
                                />
                              }
                              label="Edit"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[106]?.can_delete}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      106,
                                      "can_delete"
                                    )
                                  }
                                />
                              }
                              label="Delete"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[106]?.can_view}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      106,
                                      "can_view"
                                    )
                                  }
                                />
                              }
                              label="View"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <FormControlLabel
                      sx={{ marginTop: 1 }}
                      control={
                        <Checkbox
                          value={107}
                          checked={!!selectedProducts[107]}
                          onChange={(event) => handleProductChange(event, 107)}
                        />
                      }
                      label="Schedule Change"
                    />
                    <Grid container direction="row" spacing={2} mt={1}>
                      {selectedProducts[107] && (
                        <Grid container direction="row" ml={3} spacing={1}>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[107]?.can_add}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      107,
                                      "can_add"
                                    )
                                  }
                                />
                              }
                              label="Add"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[107]?.can_edit}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      107,
                                      "can_edit"
                                    )
                                  }
                                />
                              }
                              label="Edit"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[107]?.can_delete}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      107,
                                      "can_delete"
                                    )
                                  }
                                />
                              }
                              label="Delete"
                            />
                          </Grid>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[107]?.can_view}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      107,
                                      "can_view"
                                    )
                                  }
                                />
                              }
                              label="View"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <FormControlLabel
                      sx={{ marginTop: 1 }}
                      control={
                        <Checkbox
                          value={102}
                          checked={true}
                          onChange={(event) => handleProductChange(event, 102)}
                        />
                      }
                      label="Manage My Itinerary"
                    />
                    <Grid container direction="row" spacing={2} mt={1}>
                      {/* {selectedProducts[102] && ( */}
                      <Grid container direction="row" ml={3} spacing={1}>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={true}
                                onChange={(event) =>
                                  handlePermissionChange(event, 102, "can_view")
                                }
                              />
                            }
                            label="View"
                          />
                        </Grid>
                      </Grid>
                      {/* )} */}
                    </Grid>
                    <FormControlLabel
                      sx={{ marginTop: 1 }}
                      control={
                        <Checkbox
                          value={103}
                          checked={!!selectedProducts[103]}
                          onChange={(event) => handleProductChange(event, 103)}
                        />
                      }
                      label="Ticket Report"
                    />
                    <Grid container direction="row" spacing={2} mt={1}>
                      {selectedProducts[103] && (
                        <Grid container direction="row" ml={3} spacing={1}>
                          <Grid item>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!selectedProducts[103]?.can_view}
                                  onChange={(event) =>
                                    handlePermissionChange(
                                      event,
                                      103,
                                      "can_view"
                                    )
                                  }
                                />
                              }
                              label="View"
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={104}
                          checked={true}
                          onChange={(event) => handleProductChange(event, 104)}
                        />
                      }
                      label="Manage Customers"
                    />
                    <Grid container direction="row" spacing={2} mt={1}>
                      <Grid container direction="row" ml={3} spacing={1}>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedProducts[104]?.can_add}
                                onChange={(event) =>
                                  handlePermissionChange(event, 104, "can_add")
                                }
                              />
                            }
                            label="Add"
                          />
                        </Grid>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedProducts[104]?.can_edit}
                                onChange={(event) =>
                                  handlePermissionChange(event, 104, "can_edit")
                                }
                              />
                            }
                            label="Edit"
                          />
                        </Grid>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedProducts[104]?.can_delete}
                                onChange={(event) =>
                                  handlePermissionChange(
                                    event,
                                    104,
                                    "can_delete"
                                  )
                                }
                              />
                            }
                            label="Delete"
                          />
                        </Grid>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={true}
                                onChange={(event) =>
                                  handlePermissionChange(event, 104, "can_view")
                                }
                              />
                            }
                            label="View"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </Grid>
            ) : null}

            {role === "admin" ? (
              <FormControl>
                <FormLabel sx={{ color: "black !important" }}>
                  Admins Access
                </FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        value={105}
                        checked={
                          !!selectedProducts[105] &&
                          (selectedProducts[105].can_add ||
                            selectedProducts[105].can_edit ||
                            selectedProducts[105].can_delete ||
                            selectedProducts[105].can_view)
                        }
                        onChange={(event) => handleProductChange(event, 105)}
                      />
                    }
                    label="Manage Customer Logins"
                  />
                  <Grid container direction="row" spacing={2} mt={1}>
                    {selectedProducts[105] && (
                      <Grid container direction="row" ml={3} spacing={1}>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedProducts[105]?.can_add}
                                onChange={(event) =>
                                  handlePermissionChange(event, 105, "can_add")
                                }
                              />
                            }
                            label="Add"
                          />
                        </Grid>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedProducts[105]?.can_edit}
                                onChange={(event) =>
                                  handlePermissionChange(event, 105, "can_edit")
                                }
                              />
                            }
                            label="Activate/Deactivate"
                          />
                        </Grid>

                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!selectedProducts[105]?.can_view}
                                onChange={(event) =>
                                  handlePermissionChange(event, 105, "can_view")
                                }
                              />
                            }
                            label="View"
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </FormGroup>
              </FormControl>
            ) : null}

            <Grid item xs={12} mt={1}>
              <FormControl>
                <FormLabel sx={{ color: "black !important" }}>
                  Country Scope
                </FormLabel>
                <RadioGroup
                  row
                  defaultValue={countryScope}
                  value={countryScope}
                  onChange={(e) => setCountryScope(e.target.value)}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="Select All Countries"
                  />
                  <FormControlLabel
                    value="multiple"
                    control={<Radio />}
                    label="Select Multiple Countries"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {countryScope === "multiple" && (
              <>
                <Grid item xs={12} mt={2}>
                  <Grid item xs={6}>
                    Country
                  </Grid>
                  <Grid item xs={6} mt={1}>
                    <Autocomplete
                      fullWidth
                      multiple
                      options={countryInfo || []}
                      getOptionLabel={(option) => option.name.trim()}
                      value={
                        selectedCountries
                          ?.map(
                            (selected) =>
                              countryInfo?.find((country) =>
                                country?.country_code?.trim() === selected
                                  ? country.country_code.trim()
                                  : null
                              ) || null
                          )
                          .filter(Boolean) || []
                      }
                      onChange={handleSetSelectedCountries}
                      renderInput={(params) => (
                        <TextField {...params} variant="outlined" />
                      )}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Grid item xs={12} mt={2}>
              <Grid item xs={6}>
                Contact Number
              </Grid>
              <Grid item xs={6} mt={1}>
                <TextField
                  fullWidth
                  value={contact_number}
                  inputProps={{ maxLength: 13 }}
                  onChange={handleContactNumber}
                  error={!!contact_number_error}
                  helperText={contact_number_error}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt={2}>
              <Button
                variant="contained"
                type="submit"
                sx={{ backgroundColor: "#282828" }}
                onClick={handleSubmit}
              >
                Save
              </Button>
            </Grid>
          </Grid>
          {userInfo && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Success</AlertTitle>
                {userInfo}
              </Alert>
            </Snackbar>
          )}
          {error && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            </Snackbar>
          )}
          {errorAlert && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                <AlertTitle>{errorAlertTitle}</AlertTitle>
                {errorAlertMessage}
              </Alert>
            </Snackbar>
          )}
          {success && message && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="success"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Success</AlertTitle>
                {message}
              </Alert>
            </Snackbar>
          )}
          {updateUserError && (
            <Snackbar
              open={snackOpen}
              autoHideDuration={5000}
              onClose={handleSnackClose}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ width: "20%" }}
            >
              <Alert
                onClose={handleSnackClose}
                severity="error"
                sx={{ width: "100%" }}
              >
                <AlertTitle>Error</AlertTitle>
                {updateUserError}
              </Alert>
            </Snackbar>
          )}
        </Box>
      </Box>
    </Container>
  );
}
